<template>
    <section class="login-page">
        <div class="container login-container">
            <div class="row align-items-center justify-content-center g-lg-5">

                <!-- Information desktop -->
                <div class="col-lg-6 d-none d-lg-block">
                    <div class="login-info">
                        <div class="login-brand">
                            <img src="/img/logo.png" alt="Logo Argos" class="login-brand-logo" />

                            <div>
                                <div class="login-brand-name">
                                    Argos
                                </div>

                                <div class="login-brand-subtitle">
                                    Espace sécurisé
                                </div>
                            </div>
                        </div>

                        <h1 class="login-info-title">
                            Bienvenue sur votre espace Argos
                        </h1>

                        <p class="login-info-text">
                            Connectez-vous avec les identifiants qui vous ont été
                            fournis par votre administrateur.
                        </p>

                        <div class="login-info-separator"></div>

                        <p class="login-info-secondary">
                            Pour obtenir des identifiants de connexion, contactez
                            votre administrateur.
                        </p>

                        <p class="login-info-secondary mb-0">
                            Si vous n'êtes pas client, retrouvez-nous sur
                            <a href="https://sms-active.com" target="_blank" rel="noopener noreferrer"
                                class="login-info-link">
                                sms-active.com
                            </a>
                        </p>
                    </div>
                </div>

                <!-- Formulaire -->
                <div class="col-12 col-sm-10 col-md-8 col-lg-6">
                    <div class="card login-card">
                        <div class="card-body login-card-body">

                            <div class="login-card-header">
                                <img src="/img/logo.png" alt="Logo Argos" class="login-logo" />

                                <div>
                                    <h2 class="login-title">
                                        Connexion
                                    </h2>

                                    <p class="login-subtitle">
                                        Accédez à votre espace sécurisé
                                    </p>
                                </div>
                            </div>

                            <Form class="login-form" :validation-schema="schema" @submit="handleLogin">
                                <!-- Email -->
                                <div class="login-field">
                                    <label for="emailInput" class="form-label login-label">
                                        Adresse e-mail
                                    </label>

                                    <Field id="emailInput" name="email" type="text" class="form-control login-input"
                                        placeholder="Votre adresse e-mail" autocomplete="username" />

                                    <ErrorMessage name="email" class="login-error" />
                                </div>

                                <!-- Mot de passe -->
                                <div class="login-field">
                                    <label for="passwordInput" class="form-label login-label">
                                        Mot de passe
                                    </label>

                                    <div class="password-wrapper">
                                        <Field id="passwordInput" name="password"
                                            :type="showPassword ? 'text' : 'password'"
                                            class="form-control login-input password-input"
                                            placeholder="Votre mot de passe" autocomplete="current-password" />

                                        <button type="button" class="password-toggle" :aria-label="showPassword
                                            ? 'Masquer le mot de passe'
                                            : 'Afficher le mot de passe'
                                            " :title="showPassword
                                                ? 'Masquer le mot de passe'
                                                : 'Afficher le mot de passe'
                                                " @click="showPassword = !showPassword">
                                            <font-awesome-icon :icon="[
                                                'fas',
                                                showPassword
                                                    ? 'eye-slash'
                                                    : 'eye'
                                            ]" />
                                        </button>
                                    </div>

                                    <ErrorMessage name="password" class="login-error" />
                                </div>

                                <!-- Erreur de connexion -->
                                <div v-if="message" class="alert alert-danger login-alert" role="alert">
                                    {{ message }}
                                </div>

                                <!-- Se souvenir de moi -->
                                <div class="form-check login-remember">
                                    <input id="rememberMe" class="form-check-input" type="checkbox"
                                        value="remember-me" />

                                    <label class="form-check-label" for="rememberMe">
                                        Se souvenir de moi
                                    </label>
                                </div>

                                <!-- Bouton -->
                                <button type="submit" class="btn btn-primary login-submit">
                                    Se connecter
                                </button>

                                <!-- Information mobile -->
                                <div class="login-mobile-info d-lg-none">
                                    <div v-if="!isNativeApp" class="login-mobile-stores">
                                        <a href="https://apps.apple.com/fr/app/argos-by-sms/id6759922127"
                                            target="_blank" rel="noopener noreferrer">
                                            <img src="/img/app-store.svg" alt="Télécharger sur l'App Store" />
                                        </a>
                                        
                                        <a href="https://play.google.com/store/apps/details?id=com.reponse42.argos"
                                            target="_blank" rel="noopener noreferrer">
                                            <img src="/img/google-play.svg" alt="Disponible sur Google Play" />
                                        </a>
                                    </div>

                                    <div class="login-mobile-separator"></div>

                                    <p>
                                        Pour obtenir des identifiants de connexion,
                                        contactez votre administrateur.
                                    </p>

                                    <p class="mb-0">
                                        Si vous n'êtes pas client, retrouvez-nous sur
                                        <a href="https://sms-active.com" target="_blank" rel="noopener noreferrer">
                                            sms-active.com
                                        </a>
                                    </p>
                                </div>
                            </Form>
                        </div>

                        <div class="card-footer login-version">
                            v{{ $version }}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </section>
</template>

<script>
import { Form, Field, ErrorMessage } from "vee-validate";
import * as yup from "yup";
import { Capacitor } from "@capacitor/core";

export default {
    components: {
        Form,
        Field,
        ErrorMessage,
    },

    data() {
        const schema = yup.object().shape({
            email: yup
                .string()
                .required("Une adresse e-mail est requise"),

            password: yup
                .string()
                .required("Veuillez entrer un mot de passe"),
        });

        return {
            message: "",
            schema,
            showPassword: false,
        };
    },

    computed: {
        isNativeApp() {
            return Capacitor.isNativePlatform();
        },
        loggedIn() {
            return this.$store.state.auth.status.loggedIn;
        },
    },

    created() {
        if (this.loggedIn) {
            this.redirectAfterLogin();
        }
    },

    methods: {
        redirectAfterLogin() {
            const fallback =
                this.$isAdmin || this.$isOperator
                    ? "/baliseslistall"
                    : "/home";

            const target = this.$route.query.from || fallback;

            this.$router.replace(target);
        },

        handleLogin(user) {
            this.message = "";

            this.$store
                .dispatch("auth/login", user)
                .then(() => {
                    this.redirectAfterLogin();
                })
                .catch((error) => {
                    const status = error?.response?.status;

                    if (status === 401 || status === 404) {
                        this.message =
                            "Erreur de connexion, veuillez vérifier vos identifiants.";
                        return;
                    }

                    this.message =
                        "Une erreur est survenue lors de la connexion. Veuillez réessayer.";
                });
        },
    },
};
</script>

<style>
.login-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    overflow: hidden;
    padding: 2rem 0;

    background-color: hsl(218, 41%, 15%);
    background-image:
        linear-gradient(90deg,
            rgba(8, 24, 48, 0.96) 0%,
            rgba(8, 24, 48, 0.88) 45%,
            rgba(8, 24, 48, 0.66) 100%),
        url("../assets/logscreen.jpg");

    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
}

.login-container {
    padding-left: 2rem;
    padding-right: 2rem;
}

/* Partie informative desktop */

.login-info {
    max-width: 560px;
    color: #ffffff;
}

.login-brand {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2.5rem;
}

.login-brand-logo {
    width: auto;
    height: 58px;
    object-fit: contain;
}

.login-brand-name {
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 1.1;
}

.login-brand-subtitle {
    margin-top: 0.25rem;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.92rem;
}

.login-info-title {
    max-width: 520px;
    margin-bottom: 1.25rem;
    color: hsl(218, 81%, 95%);
    font-size: clamp(2.25rem, 4vw, 3.75rem);
    font-weight: 700;
    line-height: 1.08;
    letter-spacing: -0.04em;
}

.login-info-text {
    max-width: 530px;
    margin-bottom: 0;
    color: hsl(218, 50%, 88%);
    font-size: 1.08rem;
    line-height: 1.7;
}

.login-info-separator {
    width: 72px;
    height: 2px;
    margin: 2rem 0;
    border-radius: 999px;
    background: hsl(218, 81%, 75%);
}

.login-info-secondary {
    max-width: 520px;
    color: rgba(255, 255, 255, 0.75);
    font-size: 0.95rem;
    line-height: 1.6;
}

.login-info-link {
    color: #ffffff;
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 3px;
}

.login-info-link:hover {
    color: hsl(218, 81%, 80%);
}

/* Carte */

.login-card {
    width: 100%;
    max-width: 460px;
    margin-left: auto;
    overflow: hidden;

    border: 1px solid rgba(255, 255, 255, 0.45);
    border-radius: 20px;

    background: rgba(255, 255, 255, 0.96);
    box-shadow:
        0 24px 70px rgba(0, 0, 0, 0.28),
        0 4px 18px rgba(0, 0, 0, 0.12);

    backdrop-filter: blur(18px);
}

.login-card-body {
    padding: 2.25rem;
}

.login-card-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2rem;
}

.login-logo {
    width: auto;
    height: 52px;
    object-fit: contain;
}

.login-title {
    margin: 0;
    color: #172033;
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 1.2;
}

.login-subtitle {
    margin: 0.3rem 0 0;
    color: #6c757d;
    font-size: 0.92rem;
}

/* Formulaire */

.login-form {
    width: 100%;
}

.login-field {
    margin-bottom: 1.25rem;
}

.login-label {
    margin-bottom: 0.45rem;
    color: #303849;
    font-size: 0.9rem;
    font-weight: 600;
}

.login-input {
    min-height: 48px;
    border: 1px solid #d8dee8;
    border-radius: 10px;
    padding: 0.7rem 0.9rem;
    color: #172033;
    font-size: 0.95rem;
    box-shadow: none;
    transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease;
}

.login-input::placeholder {
    color: #9aa3b2;
}

.login-input:focus {
    border-color: #0d6efd;
    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.12);
}

.password-wrapper {
    position: relative;
}

.password-input {
    padding-right: 3rem;
}

.password-toggle {
    position: absolute;
    top: 50%;
    right: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;

    width: 34px;
    height: 34px;
    padding: 0;

    transform: translateY(-50%);

    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #6c757d;
    cursor: pointer;
    transition:
        color 0.2s ease,
        background-color 0.2s ease;
}

.password-toggle:hover {
    background: #edf1f6;
    color: #172033;
}

.password-toggle:focus-visible {
    outline: 2px solid #0d6efd;
    outline-offset: 2px;
}

.login-error {
    display: block;
    margin-top: 0.4rem;
    color: #dc3545;
    font-size: 0.82rem;
}

.login-alert {
    margin-bottom: 1.25rem;
    padding: 0.8rem 0.9rem;
    border-radius: 10px;
    font-size: 0.9rem;
}

.login-remember {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    margin-bottom: 1.35rem;
    color: #4d5665;
    font-size: 0.9rem;
}

.login-remember .form-check-input {
    margin-top: 0;
}

.login-submit {
    width: 100%;
    min-height: 48px;
    border-radius: 10px;
    font-weight: 600;
}

/* Information mobile */

.login-mobile-stores {
    display: flex;
    justify-content: center;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
    flex-wrap: wrap;
}

.login-mobile-stores a {
    display: block;
    transition: transform 0.15s ease;
}

.login-mobile-stores a:active {
    transform: scale(0.97);
}

.login-mobile-stores img {
    display: block;
    width: auto;
    height: 48px;
}

.login-mobile-info {
    margin-top: 1.5rem;
    color: #6c757d;
    text-align: center;
    font-size: 0.84rem;
    line-height: 1.55;
}

.login-mobile-info p {
    margin-bottom: 0.5rem;
}

.login-mobile-info a {
    color: #0d6efd;
    font-weight: 600;
    text-decoration: none;
}

.login-mobile-info a:hover {
    text-decoration: underline;
}

.login-mobile-separator {
    width: 48px;
    height: 1px;
    margin: 0 auto 1.25rem;
    background: #dfe4eb;
}

/* Version */

.login-version {
    padding: 0 1.5rem 1.15rem;
    border-top: 0;
    background: transparent;
    color: #8a93a2;
    font-size: 0.76rem;
    text-align: center;
}

/* Écrans de faible hauteur */

@media (max-height: 820px) and (min-width: 992px) {
    .login-page {
        padding: 1rem 0;
    }

    .login-brand {
        margin-bottom: 1.5rem;
    }

    .login-info-title {
        margin-bottom: 0.8rem;
        font-size: clamp(2rem, 3.5vw, 3rem);
    }

    .login-info-separator {
        margin: 1.25rem 0;
    }

    .login-card-body {
        padding-top: 1.5rem;
        padding-bottom: 1.5rem;
    }

    .login-card-header {
        margin-bottom: 1.35rem;
    }

    .login-field {
        margin-bottom: 1rem;
    }

    .login-remember {
        margin-bottom: 1rem;
    }
}

/* Tablette et mobile */

@media (max-width: 991.98px) {
    .login-page {
        min-height: 100svh;
        padding: 1.5rem 0;
        overflow-y: auto;

        background-image:
            linear-gradient(rgba(8, 24, 48, 0.78),
                rgba(8, 24, 48, 0.78)),
            url("../assets/logscreen.jpg");
    }

    .login-container {
        padding-left: 1.25rem;
        padding-right: 1.25rem;
    }

    .login-card {
        max-width: 480px;
        margin: 0 auto;
    }
}

@media (max-width: 575.98px) {
    .login-page {
        align-items: center;
        padding: 1.5rem 0;
    }

    .login-container {
        padding-left: 0.9rem;
        padding-right: 0.9rem;
    }

    .login-card {
        border-radius: 16px;
    }

    .login-card-body {
        padding: 1.5rem;
    }

    .login-card-header {
        margin-bottom: 1.5rem;
    }

    .login-logo {
        height: 44px;
    }

    .login-mobile-stores img {
        height: 44px;
    }

    .login-title {
        font-size: 1.4rem;
    }

    .login-subtitle {
        font-size: 0.85rem;
    }

    .login-field {
        margin-bottom: 1rem;
    }
}

@media (max-width: 575.98px) and (max-height: 750px) {
    .login-page {
        align-items: flex-start;
        padding-top: 1rem;
        padding-bottom: 1rem;
    }
}

/* Très petits écrans en hauteur */

@media (max-height: 650px) and (max-width: 991.98px) {
    .login-page {
        padding: 0.75rem 0;
    }

    .login-card-body {
        padding-top: 1.25rem;
        padding-bottom: 1.25rem;
    }

    .login-card-header {
        margin-bottom: 1rem;
    }

    .login-mobile-info {
        margin-top: 1rem;
    }

    .login-mobile-separator {
        margin-bottom: 0.9rem;
    }
}
</style>